import {
    Component,
    computed,
    CSSProperties,
    defineAsyncComponent,
    defineComponent,
    FunctionalComponent,
    inject,
    markRaw,
    MaybeRefOrGetter,
    onUnmounted,
    Ref,
    ref,
    SetupContext,
    shallowRef,
    Slot,
    SlotsType,
    toValue,
} from "vue";
import MagAvatarResolvingProxy from "../global/MagAvatarResolvingProxy.vue";
import MkA from "../global/MkA.vue";
import * as Misskey from "calckey-js";
import MkUserName from "../global/MkUserName.vue";
import MkEllipsis from "../global/MkEllipsis.vue";

import styles from "@/style/context-menu.module.scss";
import { packed, types } from "magnetar-common";
import MagContextMenuInner from "@/components/context-menu/MagContextMenuInner.vue";
import MagAvatar from "@/components/global/MagAvatar.vue";
import { useCountdown, whenever } from "@vueuse/core";
import { ShallowRef, useId } from "@vue/runtime-core";
import EventEmitter from "eventemitter3";
import { NoteModel } from "@/model/note";
import { MenuItem } from "./mag-dynamic-menu";
import MagCheckbox from "@/components/primitives/MagCheckbox.vue";
import { v7 } from "uuid";
import { placePopover } from "@/components/layout/popover-layer";
import { ComponentProps } from "vue-component-type-helpers";

const MagMenuSeparatorFunc: FunctionalComponent = () => {
    return () => <div class={styles.divider}></div>;
};

const MagMenuPendingFunc: FunctionalComponent = () => {
    return () => (
        <span class={{ [styles.pending]: true, [styles.item]: true }}>
            <span>
                <MkEllipsis />
            </span>
        </span>
    );
};

const MagMenuLabelFunc = (
    props: { textStyle?: MaybeRefOrGetter<CSSProperties> },
    { slots }: SetupContext<{}, SlotsType<{ default: Slot }>>,
) => {
    return () => (
        <span class={{ [styles.label]: true, [styles.item]: true }}>
            <span style={toValue(props.textStyle)}>{slots?.default?.()}</span>
        </span>
    );
};

const MagMenuLinkFunc = (
    props: {
        to: MaybeRefOrGetter<string>;
        textStyle?: MaybeRefOrGetter<CSSProperties>;
        icon?: MaybeRefOrGetter<string>;
        indicate?: MaybeRefOrGetter<boolean>;
        avatar?: MaybeRefOrGetter<packed.PackUserBase | Misskey.entities.User>;
    },
    {
        emit,
        slots,
    }: SetupContext<
        { close: (actioned: boolean) => void },
        SlotsType<{ default: Slot; subtitle: Slot }>
    >,
) => {
    const closeChildrenEmitter = inject<EventEmitter>("closeChildrenEmitter");
    const { start, stop } = useCountdown(1, {
        immediate: false,
        interval: 200,
        onComplete: () => {
            closeChildrenEmitter?.emit("close");
        },
    });

    return () => {
        return (
            <MkA
                to={toValue(props.to)}
                class={{
                    _button: true,
                    [styles.link]: true,
                    [styles.item]: true,
                }}
                onMouseenter={(e: MouseEvent) => {
                    start();
                }}
                onMouseleave={(e: MouseEvent) => {
                    stop();
                }}
                onClick={(e: MouseEvent) => {
                    emit("close", true);
                }}
            >
                {toValue(props.icon) ? (
                    <mg-icon icon={toValue(props.icon)} />
                ) : null}
                {toValue(props.avatar) ? (
                    <MagAvatarResolvingProxy
                        user={toValue(props.avatar)!}
                        class="avatar"
                        disableLink
                    />
                ) : null}
                <span class={styles.multiline}>
                    <span style={toValue(props.textStyle)}>
                        {slots?.default?.()}
                    </span>
                    {slots?.subtitle ? (
                        <span
                            class={styles.subtitle}
                            style={toValue(props.textStyle)}
                        >
                            {slots?.subtitle?.()}
                        </span>
                    ) : null}
                </span>
                {toValue(props.indicate) ? (
                    <span class={styles.indicator}>
                        <mg-icon icon="circle" iconSet="fill" />
                    </span>
                ) : null}
            </MkA>
        );
    };
};

const MagMenuAFunc = (
    props: {
        href: MaybeRefOrGetter<string>;
        target?: MaybeRefOrGetter<string>;
        download?: MaybeRefOrGetter<string>;
        textStyle?: MaybeRefOrGetter<CSSProperties>;
        icon?: MaybeRefOrGetter<string>;
        indicate?: MaybeRefOrGetter<boolean>;
    },
    {
        emit,
        slots,
    }: SetupContext<
        {
            close: (actioned: boolean) => void;
        },
        SlotsType<{ default: Slot; subtitle: Slot }>
    >,
) => {
    const closeChildrenEmitter = inject<EventEmitter>("closeChildrenEmitter");
    const { start, stop } = useCountdown(1, {
        immediate: false,
        interval: 200,
        onComplete: () => {
            closeChildrenEmitter?.emit("close");
        },
    });

    return () => (
        <a
            href={toValue(props.href)}
            target={toValue(props.target)}
            download={props.download}
            class={{
                _button: true,
                [styles.link]: true,
                [styles.button]: true,
                [styles.item]: true,
            }}
            onMouseenter={(e: MouseEvent) => {
                start();
            }}
            onMouseleave={(e: MouseEvent) => {
                stop();
            }}
            onClick={() => {
                emit("close", true);
            }}
        >
            {props.icon ? <mg-icon icon={toValue(props.icon)} /> : null}
            <span class={styles.multiline}>
                <span style={toValue(props.textStyle)}>
                    {slots?.default?.()}
                </span>
                {slots?.subtitle ? (
                    <span
                        class={styles.subtitle}
                        style={toValue(props.textStyle)}
                    >
                        {slots?.subtitle?.()}
                    </span>
                ) : null}
            </span>
            {props.indicate ? (
                <span class={styles.item}>
                    <mg-icon icon="circle" iconSet="fill" />
                </span>
            ) : null}
        </a>
    );
};

const MagMenuButtonFunc = (
    props: {
        textStyle?: MaybeRefOrGetter<CSSProperties>;
        icon?: MaybeRefOrGetter<string>;
        indicate?: MaybeRefOrGetter<boolean>;
        danger?: MaybeRefOrGetter<boolean>;
        accent?: MaybeRefOrGetter<boolean>;
        active?: MaybeRefOrGetter<boolean>;
        hidden?: MaybeRefOrGetter<boolean>;
        avatar?: MaybeRefOrGetter<
            packed.PackUserBase | Misskey.entities.User | null
        >;
    },
    {
        emit,
        slots,
    }: SetupContext<
        {
            close: (actioned: boolean) => void;
            action: (ev: MouseEvent) => void;
        },
        SlotsType<{ default: Slot; subtitle: Slot }>
    >,
) => {
    const closeChildrenEmitter = inject<EventEmitter>("closeChildrenEmitter");
    const { start, stop } = useCountdown(1, {
        immediate: false,
        interval: 200,
        onComplete: () => {
            closeChildrenEmitter?.emit("close");
        },
    });

    return () => {
        if (props.hidden) {
            return <></>;
        }

        return (
            <button
                class={{
                    _button: true,
                    [styles.button]: true,
                    [styles.item]: true,
                    [styles.active]: toValue(props.active),
                    [styles.danger]: toValue(props.danger),
                    [styles.accent]: toValue(props.accent),
                }}
                onMouseenter={(e: MouseEvent) => {
                    start();
                }}
                onMouseleave={(e: MouseEvent) => {
                    stop();
                }}
                onClick={(e: MouseEvent) => {
                    emit("action", e);
                    emit("close", true);
                }}
            >
                {toValue(props.icon) ? (
                    <mg-icon icon={toValue(props.icon)} />
                ) : null}
                {toValue(props.avatar) ? (
                    <MagAvatarResolvingProxy
                        user={toValue(props.avatar)!}
                        class="avatar"
                        disableLink
                    />
                ) : null}

                <span class={styles.multiline}>
                    <span style={toValue(props.textStyle)}>
                        {slots?.default?.()}
                    </span>
                    {slots?.subtitle ? (
                        <span
                            class={styles.subtitle}
                            style={toValue(props.textStyle)}
                        >
                            {slots?.subtitle?.()}
                        </span>
                    ) : null}
                </span>
                {toValue(props.indicate) ? (
                    <span class={styles.item}>
                        <mg-icon icon="circle" iconSet="fill" />
                    </span>
                ) : null}
            </button>
        );
    };
};

const MagMenuCheckboxFunc = (
    props: {
        textStyle?: MaybeRefOrGetter<CSSProperties>;
        icon?: MaybeRefOrGetter<string>;
        danger?: MaybeRefOrGetter<boolean>;
        accent?: MaybeRefOrGetter<boolean>;
        active?: MaybeRefOrGetter<boolean>;
        hidden?: MaybeRefOrGetter<boolean>;
        checked?: MaybeRefOrGetter<boolean>;
    },
    {
        emit,
        slots,
    }: SetupContext<
        {
            close: (actioned: boolean) => void;
            ["update:checked"]: (val: boolean) => void;
        },
        SlotsType<{ default: Slot; subtitle: Slot }>
    >,
) => {
    const closeChildrenEmitter = inject<EventEmitter>("closeChildrenEmitter");
    const { start, stop } = useCountdown(1, {
        immediate: false,
        interval: 200,
        onComplete: () => {
            closeChildrenEmitter?.emit("close");
        },
    });

    const checkedValue = ref(toValue(props.checked));
    const id = useId();

    return () => {
        if (props.hidden) {
            return <></>;
        }

        return (
            <label
                class={{
                    _button: true,
                    [styles.checkbox]: true,
                    [styles.item]: true,
                    [styles.active]: toValue(props.active),
                    [styles.danger]: toValue(props.danger),
                    [styles.accent]: toValue(props.accent),
                }}
                onMouseenter={(e: MouseEvent) => {
                    start();
                }}
                onMouseleave={(e: MouseEvent) => {
                    stop();
                }}
                for={id}
            >
                {toValue(props.icon) ? (
                    <mg-icon icon={toValue(props.icon)} />
                ) : null}
                <span class={styles.multiline}>
                    <span style={toValue(props.textStyle)}>
                        {slots?.default?.()}
                    </span>
                    {slots?.subtitle ? (
                        <span
                            class={styles.subtitle}
                            style={toValue(props.textStyle)}
                        >
                            {slots?.subtitle?.()}
                        </span>
                    ) : null}
                </span>
                <MagCheckbox
                    id={id}
                    checked={checkedValue.value}
                    onUpdate:checked={(val: boolean) => {
                        checkedValue.value = val;
                        emit("update:checked", val);
                        emit("close", true);
                    }}
                />
            </label>
        );
    };
};

const MagMenuUserFunc = (
    props: {
        active?: MaybeRefOrGetter<boolean>;
        indicate?: MaybeRefOrGetter<boolean>;
        disabled?: MaybeRefOrGetter<boolean>;
        hidden?: MaybeRefOrGetter<boolean>;
        user: MaybeRefOrGetter<packed.PackUserBase>;
    },
    context: SetupContext<{
        close: (actioned: boolean) => void;
        action: (ev: MouseEvent) => void;
    }>,
) => {
    const closeChildrenEmitter = inject<EventEmitter>("closeChildrenEmitter");
    const { start, stop } = useCountdown(1, {
        immediate: false,
        interval: 200,
        onComplete: () => {
            closeChildrenEmitter?.emit("close");
        },
    });

    return () => {
        if (toValue(props.hidden)) {
            return <></>;
        }

        return (
            <button
                class={{
                    _button: true,
                    [styles.button]: true,
                    [styles.item]: true,
                    [styles.active]: toValue(props.active),
                }}
                disabled={toValue(props.disabled)}
                onMouseenter={(e: MouseEvent) => {
                    start();
                }}
                onMouseleave={(e: MouseEvent) => {
                    stop();
                }}
                onClick={(e: MouseEvent) => {
                    context.emit("action", e);
                    context.emit("close", true);
                }}
            >
                <MagAvatar
                    user={toValue(props.user)}
                    class={styles.avatar}
                    disableLink
                />
                <MkUserName user={toValue(props.user)} />
                {toValue(props.indicate) ? (
                    <span class={styles.indicator}>
                        <mg-icon icon="circle" iconSet="fill" />
                    </span>
                ) : null}
            </button>
        );
    };
};

const MagMenuNestedFunc = (
    props: {
        textStyle?: MaybeRefOrGetter<CSSProperties>;
        icon?: MaybeRefOrGetter<string>;
        drawer?: boolean;
        danger?: boolean;
    },
    {
        emit,
        slots,
    }: SetupContext<
        { close: (actioned: boolean) => void },
        SlotsType<{
            default: Slot;
            inner: Slot;
        }>
    >,
) => {
    const parentMenu = inject<ShallowRef<Component>>("parentMenu");
    const parentClosing = inject<Ref<boolean>>("parentClosing");
    const el = shallowRef<HTMLButtonElement | null>(null);
    const closeChildrenEmitter = inject<EventEmitter>("closeChildrenEmitter");
    const childShowing = ref(false);

    whenever(childShowing, () => {
        magContextMenu({}, () => slots.inner(), {
            parent: el.value,
            parentMenu: parentMenu?.value,
            parentClosingSignal: computed(
                () => !childShowing.value || parentClosing?.value,
            ),
        });
    });

    const { start, stop } = useCountdown(1, {
        immediate: false,
        interval: 300,
        onComplete: () => {
            childShowing.value = true;
        },
    });

    const hideChild = () => {
        childShowing.value = false;
    };

    closeChildrenEmitter?.on("close", hideChild);

    onUnmounted(() => {
        closeChildrenEmitter?.off("close", hideChild);
    });

    return () => {
        return (
            <button
                ref={el}
                class={{
                    _button: true,
                    [styles.button]: true,
                    [styles.item]: true,
                    [styles.parent]: true,
                    [styles.danger]: toValue(props.danger),
                    [styles.childShowing]: childShowing.value,
                }}
                onClick={(e: MouseEvent) => {
                    if (props.drawer) {
                        emit("close", true);
                    }

                    childShowing.value = true;
                }}
                onMouseenter={(e: MouseEvent) => {
                    if (!props.drawer) {
                        if (!childShowing.value) {
                            closeChildrenEmitter?.emit("close");
                        }

                        start();
                    }
                }}
                onMouseleave={(e: MouseEvent) => {
                    stop();
                }}
            >
                {toValue(props.icon) ? (
                    <mg-icon icon={toValue(props.icon)} />
                ) : null}
                <span style={toValue(props.textStyle)}>
                    {slots?.default?.()}
                </span>
                <span class={styles.caret}>
                    <mg-icon icon="caret-right" />
                </span>
            </button>
        );
    };
};

const MagMenuSeparator = defineComponent(MagMenuSeparatorFunc, {});
const MagMenuPending = defineComponent(MagMenuPendingFunc, {});
const MagMenuLink = defineComponent(MagMenuLinkFunc, {
    props: ["to", "textStyle", "icon", "indicate", "avatar"],
    slots: Object as SlotsType<{
        default: Slot;
        subtitle: Slot;
    }>,
    emits: {
        close: (actioned: boolean) => typeof actioned === "boolean",
    },
});
const MagMenuA = defineComponent(MagMenuAFunc, {
    props: ["href", "target", "download", "textStyle", "icon", "indicate"],
    slots: Object as SlotsType<{
        default: Slot;
        subtitle: Slot;
    }>,
    emits: {
        close: (actioned: boolean) => typeof actioned === "boolean",
    },
});
const MagMenuLabel = defineComponent(MagMenuLabelFunc, {
    slots: Object as SlotsType<{
        default: Slot;
    }>,
    props: ["textStyle"],
});
const MagMenuCheckbox = defineComponent(MagMenuCheckboxFunc, {
    props: [
        "textStyle",
        "icon",
        "danger",
        "accent",
        "active",
        "hidden",
        "checked",
    ],
    slots: Object as SlotsType<{
        default: Slot;
        subtitle: Slot;
    }>,
    emits: {
        close: (actioned: boolean) => typeof actioned === "boolean",
        ["update:checked"]: (ev: boolean) => typeof ev === "boolean",
    },
});
const MagMenuButton = defineComponent(MagMenuButtonFunc, {
    props: [
        "textStyle",
        "icon",
        "indicate",
        "danger",
        "accent",
        "active",
        "hidden",
        "avatar",
    ],
    slots: Object as SlotsType<{
        default: Slot;
        subtitle: Slot;
    }>,
    emits: {
        close: (actioned: boolean) => typeof actioned === "boolean",
        action: (ev: MouseEvent) => typeof ev === "object",
    },
});
const MagMenuUser = defineComponent(MagMenuUserFunc, {
    props: ["active", "indicate", "hidden", "user"],
    emits: {
        close: (actioned: boolean) => typeof actioned === "boolean",
        action: (ev: MouseEvent) => typeof ev === "object",
    },
});
const MagMenuNested = defineComponent(MagMenuNestedFunc, {
    props: ["textStyle", "icon", "drawer", "danger"],
    slots: Object as SlotsType<{
        default: Slot;
        inner: Slot;
    }>,
    emits: {
        close: (actioned: boolean) => typeof actioned === "boolean",
    },
});

export {
    MagMenuSeparator,
    MagMenuPending,
    MagMenuLink,
    MagMenuA,
    MagMenuLabel,
    MagMenuCheckbox,
    MagMenuButton,
    MagMenuUser,
    MagMenuNested,
};

export type ContextMenu = {
    kind: "context-menu";
    id: string;
    component: FunctionalComponent<
        {},
        {
            close: (viaAction: boolean) => void;
        }
    >;
    closing: boolean;
    parent: HTMLElement | null;
    dropdown: boolean;
    isChild: boolean;
};

export type ContextMenuOptions = {
    parent?: HTMLElement | null;
    parentMenu?: Component | null;
    parentClosingSignal?: Ref<boolean> | null;
    closeCallback?: (viaAction: boolean) => void;
    dropdown?: boolean;
    fromContextMenu?: MouseEvent;
    exceptGraphic?: boolean;
};

export const contextMenuDefaults = {
    parent: null,
    parentMenu: null,
    parentClosingSignal: null,
    closeCallback: undefined,
    dropdown: false,
    fromContextMenu: undefined,
    exceptGraphic: false,
} satisfies ContextMenuOptions;

export function magContextMenu<T>(
    message: ComponentProps<T> & Record<string, unknown>,
    adapter: T & Component<ComponentProps<T>>,
    menuOptions?: ContextMenuOptions,
) {
    const options = {
        ...contextMenuDefaults,
        ...menuOptions,
    } as Required<ContextMenuOptions>;

    if (options.fromContextMenu) {
        if (options.fromContextMenu.shiftKey) {
            return;
        }

        if (options.fromContextMenu.target && options.exceptGraphic) {
            const el = options.fromContextMenu.target as Element;

            if (
                ["INPUT", "TEXTAREA", "IMG", "VIDEO", "CANVAS"].includes(
                    el.tagName,
                ) ||
                el.attributes["contenteditable"]
            ) {
                return;
            }
        }

        options.fromContextMenu.preventDefault();
        options.fromContextMenu.stopPropagation();
    }

    const id = v7();

    const component = (
        {},
        context: Omit<
            SetupContext<{ close(viaAction: boolean): void }>,
            "expose"
        >,
    ) => {
        return (
            <MagContextMenuInner
                onClose={(viaAction: boolean) => {
                    context.emit("close", viaAction);
                    options.closeCallback?.(viaAction);
                }}
                parent={options.parent}
                dropdown={options.dropdown}
                parentMenu={options.parentMenu}
                fromContextMenu={options.fromContextMenu}
                parentClosingSignal={options.parentClosingSignal?.value ?? null}
                component={adapter}
                innerProps={message}
            />
        );
    };

    component.props = {};
    component.emits = {
        close: (_viaAction: boolean) => {},
    };

    placePopover({
        kind: "context-menu",
        id,
        component: markRaw(component),
        parent: options.parent,
        dropdown: options.dropdown ?? false,
        isChild: Boolean(options.parentMenu),
        closing: false,
    });
}

export const ContextMenuNote = defineComponent(
    (
        props: {
            note: packed.PackNoteMaybeFull;
            translating: Ref<boolean>;
            translation: Ref<boolean>;
            isDeleted: Ref<boolean>;
            currentClipPage?: Misskey.entities.Clip;
            menuButton?: HTMLElement | null;
        },
        context: SetupContext<{ close: (viaAction: boolean) => void }>,
    ) => {
        const MagNoteMenu = defineAsyncComponent(
            () => import("@/components/context-menu/MagNoteMenu.vue"),
        );

        return () => (
            <MagNoteMenu
                onClose={(viaAction: boolean) =>
                    context.emit("close", viaAction)
                }
                {...props}
            />
        );
    },
);

export const ContextMenuUser = defineComponent(
    (
        props: {
            user: packed.PackUserMaybeAll;
        },
        context: SetupContext<{ close: (viaAction: boolean) => void }>,
    ) => {
        const MagUserMenu = defineAsyncComponent(
            () => import("@/components/context-menu/MagUserMenu.vue"),
        );

        return () => (
            <MagUserMenu
                onClose={(viaAction: boolean) =>
                    context.emit("close", viaAction)
                }
                {...props}
            />
        );
    },
);

export const ContextMenuRenote = defineComponent(
    (
        props: {
            note: ReturnType<typeof NoteModel>;
            hasRenoted: Ref<boolean>;
        },
        context: SetupContext<{
            close: (viaAction: boolean) => void;
            "update:hasRenoted": (value: boolean) => void;
        }>,
    ) => {
        const MagRenoteMenu = defineAsyncComponent(
            () => import("@/components/context-menu/MagRenoteMenu.vue"),
        );

        return () => (
            <MagRenoteMenu
                onClose={(viaAction: boolean) =>
                    context.emit("close", viaAction)
                }
                hasRenoted={props.hasRenoted.value}
                onUpdate:hasRenoted={(value) => {
                    context.emit("update:hasRenoted", value);
                    props.hasRenoted.value = true;
                }}
                note={props.note}
            />
        );
    },
);

export const ContextMenuAccountSwitcher = defineComponent(
    (
        props: {
            picker?: (account: packed.PackUserBase) => void;
        },
        context: SetupContext<{
            close: (viaAction: boolean) => void;
        }>,
    ) => {
        const MagAccountSwitcher = defineAsyncComponent(
            () => import("@/components/MagAccountSwitcher.vue"),
        );

        return () => (
            <MagAccountSwitcher
                onClose={(viaAction: boolean) =>
                    context.emit("close", viaAction)
                }
                picker={props.picker}
                {...props}
            />
        );
    },
);

export const ContextMenuHelp = defineComponent(
    (
        props: {},
        context: SetupContext<{
            close: (viaAction: boolean) => void;
        }>,
    ) => {
        const MagHelpMenu = defineAsyncComponent(
            () => import("@/components/context-menu/MagHelpMenu.vue"),
        );

        return () => (
            <MagHelpMenu
                onClose={(viaAction: boolean) =>
                    context.emit("close", viaAction)
                }
                {...props}
            />
        );
    },
);

export const ContextMenuDynamic = defineComponent(
    (
        props: {
            items: MenuItem[];
        },
        context: SetupContext<{
            close: (viaAction: boolean) => void;
        }>,
    ) => {
        const MagDynamicMenu = defineAsyncComponent(
            () => import("@/components/context-menu/MagDynamicMenu.vue"),
        );

        return () => (
            <MagDynamicMenu
                onClose={(viaAction: boolean) =>
                    context.emit("close", viaAction)
                }
                {...props}
            />
        );
    },
);

export const ContextMenuNoteVisibility = defineComponent(
    (
        props: {
            visibility: Ref<types.NoteVisibility>;
            localOnly: Ref<boolean>;
        },
        context: SetupContext<{
            close: (viaAction: boolean) => void;
            "update:visibility": (value: types.NoteVisibility) => void;
            "update:localOnly": (value: boolean) => void;
        }>,
    ) => {
        const MagVisibilityMenu = defineAsyncComponent(
            () => import("@/components/context-menu/MagVisibilityMenu.vue"),
        );

        const model = {
            localOnly: toValue(props.localOnly),
            "onUpdate:localOnly": (value) => {
                context.emit("update:localOnly", value);
                props.localOnly.value = value;
            },
            visibility: toValue(props.visibility),
            "onUpdate:visibility": (value) => {
                context.emit("update:visibility", value);
                props.visibility.value = value;
            },
        };

        return () => (
            <MagVisibilityMenu
                onClose={(viaAction: boolean) =>
                    context.emit("close", viaAction)
                }
                {...model}
            />
        );
    },
);
